import React, { useEffect, useMemo, useState } from 'react';
import { FiCreditCard, FiHexagon, FiDollarSign, FiClock, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import WalletBadge from '../../components/WalletBadge/WalletBadge';
import DepositModal from '../../components/DepositModal/DepositModal';
import CryptoModal from '../../components/CryptoModal/CryptoModal';
import KycBanner from '../../components/KycBanner/KycBanner';
import { useCurrency } from '../../app/CurrencyContext';
import styles from './InvestmentDashboard.module.css';
import axiosClient from '../../api/axiosClient';

interface Request {
  id: string; amount: string; amount_inr_equivalent: string | null; exchange_rate: string | null; status: string; currency: string;
  payment_method_type: string | null; transaction_ref: string | null; created_at: string; type: string;
}

const STATUS_COLORS: Record<string, string> = {
  pending: '#e8a86a', processing: '#1d60f6', paid: '#5cb87d', rejected: '#e2534a',
};
const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending', processing: 'Processing', paid: 'Approved', rejected: 'Rejected',
};

const ChartTooltip = ({ active, payload, label }: any) => {
  const { format } = useCurrency();
  if (!active || !payload || !payload.length) return null;
  return (
    <div className={styles.tooltip}>
      <b>{label}</b>
      <span>Amount: {format(Number(payload[0].value))}</span>
    </div>
  );
};

const InvestmentDashboard: React.FC = () => {
  const { convertTransaction, format, selected, inrPerSelected, currencies: adminCurrencies } = useCurrency();
  const [showFiat, setShowFiat] = useState(false);
  const [showCrypto, setShowCrypto] = useState(false);
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    axiosClient.get('/funds/mine').then(({ data }) => {
      setRequests(data.requests.filter((r: Request) => r.type === 'deposit'));
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const paidRequests = requests.filter((r) => r.status === 'paid');
  const totalDeposited = paidRequests.reduce((s, r) => s + convertTransaction(Number(r.amount), r.currency, Number(r.amount_inr_equivalent ?? r.amount)), 0);
  const pendingCount = requests.filter((r) => r.status === 'pending').length;
  const approvedCount = requests.filter((r) => r.status === 'paid').length;
  const rejectedCount = requests.filter((r) => r.status === 'rejected').length;

  const barData = useMemo(() => {
    const byDate: Record<string, { label: string; amount: number; sortKey: number }> = {};
    paidRequests.forEach((r) => {
      const dateObj = new Date(r.created_at);
      const key = dateObj.toISOString().slice(0, 10); // YYYY-MM-DD, safe for grouping across years
      const label = dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
      if (!byDate[key]) byDate[key] = { label, amount: 0, sortKey: dateObj.getTime() };
      byDate[key].amount += convertTransaction(Number(r.amount), r.currency, Number(r.amount_inr_equivalent ?? r.amount));
    });
    return Object.values(byDate)
      .sort((a, b) => a.sortKey - b.sortKey)
      .map(({ label, amount }) => ({ date: label, amount }));
  }, [paidRequests, convertTransaction]);

  return (
    <DashboardShell>
      <PageHeader title="Deposit" subtitle="Choose how you'd like to add funds, and track your requests below." action={<WalletBadge />} />

      <div className={styles.page}>
        <KycBanner />
        <div className={styles.statsGrid}>
          <div className={styles.statPill}>
            <span>Total Deposited</span>
            <b>{format(totalDeposited)}</b>
            {selected !== 'INR' && inrPerSelected && (
              <small style={{ display: 'block', fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>
                rate: 1 {selected} = ₹{inrPerSelected.toFixed(2)}
              </small>
            )}
            <FiDollarSign className={styles.statIcon} />
          </div>
          <div className={`${styles.statPill} ${styles.borderWarn}`}>
            <span>Pending</span>
            <b className={styles.warnText}>{pendingCount}</b>
            <FiClock className={`${styles.statIcon} ${styles.iconWarn}`} />
          </div>
          <div className={`${styles.statPill} ${styles.borderSuccess}`}>
            <span>Approved</span>
            <b className={styles.successText}>{approvedCount}</b>
            <FiCheckCircle className={`${styles.statIcon} ${styles.iconSuccess}`} />
          </div>
          <div className={`${styles.statPill} ${styles.borderDanger}`}>
            <span>Rejected</span>
            <b className={styles.dangerText}>{rejectedCount}</b>
            <FiXCircle className={`${styles.statIcon} ${styles.iconDanger}`} />
          </div>
        </div>

        <div className={styles.mainGrid}>
          <div className={styles.optionCol}>
            <button className={styles.optionCardSmall} onClick={() => setShowFiat(true)}>
              <div className={`${styles.optionIcon} ${styles.optionIconGreen}`}><FiCreditCard /></div>
              <div>
                <b>Fiat</b>
                <span>Bank Transfer or UPI</span>
              </div>
            </button>
            <button className={styles.optionCardSmall} onClick={() => setShowCrypto(true)}>
              <div className={`${styles.optionIcon} ${styles.optionIconBlue}`}><FiHexagon /></div>
              <div>
                <b>Crypto</b>
                <span>BNB Chain wallet</span>
              </div>
            </button>
          </div>

          <div className={styles.card}>
            <h3>Deposits Over Time</h3>
            {barData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.08)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--muted)' }} axisLine={{ stroke: 'rgba(255,255,255,.1)' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(29,96,246,.08)' }} />
                  <Bar dataKey="amount" fill="#1d60f6" radius={[6, 6, 0, 0]} maxBarSize={60}>
                    <LabelList dataKey="amount" position="top" formatter={(v: number) => format(v)} fill="var(--cream)" fontSize={11} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className={styles.chartEmpty}>No deposits yet — your activity will show up here.</div>
            )}
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.historyHeader}>
            <h3>Your Requests</h3>
            {requests.length > 0 && <span className={styles.totalBadge}>Total: {format(totalDeposited)}</span>}
          </div>
          {loading && <div className={styles.chartEmpty}>Loading…</div>}
          {!loading && requests.length === 0 && (
            <div className={styles.chartEmpty}>No deposit requests yet — use Fiat or Crypto above to get started.</div>
          )}
          {!loading && requests.length > 0 && (
            <div className={styles.list}>
              {requests.map((r) => (
                <div key={r.id} className={styles.row}>
                  <div>
                    <b>{adminCurrencies.find((c) => c.code === r.currency)?.symbol || r.currency + ' '}{Number(r.amount).toLocaleString()}</b>
                    <div className={styles.rowSub}>
                      {r.payment_method_type || 'N/A'} · {r.transaction_ref || '—'} · {new Date(r.created_at).toLocaleString()}
                      {r.currency !== 'INR' && r.amount_inr_equivalent && Number(r.amount) > 0 && (
                        <> · rate: 1 {r.currency} = ₹{(Number(r.amount_inr_equivalent) / Number(r.amount)).toFixed(2)}</>
                      )}
                    </div>
                  </div>
                  <span className={styles.statusPill} style={{ color: STATUS_COLORS[r.status], borderColor: STATUS_COLORS[r.status], background: `${STATUS_COLORS[r.status]}1a` }}>
                    {STATUS_LABELS[r.status] || r.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showFiat && <DepositModal onClose={() => setShowFiat(false)} onSuccess={load} />}
      {showCrypto && <CryptoModal onClose={() => setShowCrypto(false)} onSuccess={load} />}
    </DashboardShell>
  );
};

export default InvestmentDashboard;
